import {
  BarChart3,
  CalendarClock,
  CalendarPlus,
  LayoutDashboard,
  ListChecks,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react'
import type { Capability } from '@/api/schema'

/**
 * Navegação final do v2 (DEC-006). Fixa no shell: as features só preenchem as rotas.
 * Wocflows e as demais páginas do legado fora do escopo NÃO entram aqui.
 */

export interface NavLeaf {
  kind: 'leaf'
  id: string
  label: string
  to: string
  icon: LucideIcon
  permission: Capability
  /** `id` do manifesto da feature que atende esta rota (app/features/types.ts). */
  featureId: string
  description?: string
}

export interface NavGroup {
  kind: 'group'
  id: string
  label: string
  icon?: LucideIcon
  children: NavNode[]
}

export type NavNode = NavLeaf | NavGroup

export const ROUTES = {
  home: '/',
  scheduleRequests: '/operational/schedules/requests',
  scheduleGenerate: '/operational/schedules/generate',
  metabaseDashboards: '/metabase/dashboards',
  metabaseIndicators: '/metabase/indicators',
  integrationVerification: '/integration/verification',
} as const

export const DEFAULT_ROUTE = ROUTES.scheduleRequests

export const NAVIGATION: NavGroup[] = [
  {
    kind: 'group',
    id: 'operacional',
    label: 'Operacional',
    children: [
      {
        kind: 'group',
        id: 'agendamentos',
        label: 'Agendamentos',
        icon: CalendarClock,
        children: [
          {
            kind: 'leaf',
            id: 'pedidos',
            label: 'Pedidos',
            to: ROUTES.scheduleRequests,
            icon: ListChecks,
            permission: 'schedule:read',
            featureId: 'schedules-requests',
            description: 'Submissões de geração de agenda e status por empresa e vendedor.',
          },
          {
            kind: 'leaf',
            id: 'gerar',
            label: 'Gerar',
            to: ROUTES.scheduleGenerate,
            icon: CalendarPlus,
            permission: 'schedule:create',
            featureId: 'schedules-generate',
            description: 'Monte o plano e dispare a geração de agendas.',
          },
        ],
      },
    ],
  },
  {
    kind: 'group',
    id: 'metabase',
    label: 'Metabase',
    children: [
      {
        kind: 'leaf',
        id: 'dashboards',
        label: 'Dashboards',
        to: ROUTES.metabaseDashboards,
        icon: LayoutDashboard,
        permission: 'metabase:read',
        featureId: 'metabase',
        description: 'Catálogo de dashboards Metabase disponíveis no GoVendas.',
      },
      {
        kind: 'leaf',
        id: 'indicadores',
        label: 'Indicadores',
        to: ROUTES.metabaseIndicators,
        icon: BarChart3,
        permission: 'metabase:read',
        featureId: 'metabase',
        description: 'Catálogo de indicadores Metabase disponíveis no GoVendas.',
      },
    ],
  },
  {
    kind: 'group',
    id: 'integracao',
    label: 'Integração',
    children: [
      {
        kind: 'leaf',
        id: 'verificacao',
        label: 'Verificação',
        to: ROUTES.integrationVerification,
        icon: ShieldCheck,
        permission: 'integration:read',
        featureId: 'integration-verification',
        description: 'Paridade Scylla → GoVendas e Scylla → ETL.',
      },
    ],
  },
]

/** Todas as folhas com a trilha de grupos (para breadcrumbs e títulos). */
export function flattenNavigation(
  nodes: NavNode[] = NAVIGATION,
  trail: string[] = [],
): { leaf: NavLeaf; trail: string[] }[] {
  return nodes.flatMap((node) =>
    node.kind === 'leaf'
      ? [{ leaf: node, trail }]
      : flattenNavigation(node.children, [...trail, node.label]),
  )
}

/** Folha que corresponde ao caminho (prefixo mais longo). */
export function findNavLeaf(pathname: string) {
  const matches = flattenNavigation().filter(
    ({ leaf }) => pathname === leaf.to || pathname.startsWith(`${leaf.to}/`),
  )
  return matches.sort((a, b) => b.leaf.to.length - a.leaf.to.length)[0] ?? null
}

/** Remove folhas sem permissão e grupos que ficaram vazios. */
export function filterNavigation<T extends NavNode>(
  nodes: T[],
  can: (permission: Capability) => boolean,
): T[] {
  const result: T[] = []
  for (const node of nodes) {
    if (node.kind === 'leaf') {
      if (can(node.permission)) result.push(node)
      continue
    }
    const children = filterNavigation(node.children, can)
    if (children.length > 0) result.push({ ...node, children })
  }
  return result
}
